import apiClient from './client';

export type ChatScope = 'all' | 'documents' | 'public';
export type ChatModelPath = 'auto' | 'private' | 'public';

export interface ChatCitation {
  type: 'document' | 'public' | 'case_fact';
  ref: string;
  snippet?: string;
  filename?: string;
  source_url?: string;
  washed?: boolean;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  attachments?: string[]; // document ids attached to a user message
  // Metadata attached to assistant answers.
  path_used?: string | null;
  model_used?: string | null;
  run_id?: string | null;
  citations?: ChatCitation[];
  scope?: ChatScope;
  // DataWash rebuild: flags for reconstructed output / unknown tags.
  reconstructed?: boolean;
  unknown_tags?: string[];
  warnings?: string[];
  // DataWash rebuild: a result-card link target (e.g. 'overview', 'diff',
  // 'documents') rendered as a clickable link in the chat bubble.
  result_link?: string | null;
  // DataWash rebuild (UC-1.8): when true, this system message renders as a
  // "Re-run deficiency analysis?" prompt with Re-run / Skip action buttons.
  rerun_prompt?: boolean;
  // case-request ledger (2026-10-21): ties this message to a durable
  // case_request row so pending placeholders can be swapped for the
  // completed answer/result when the poll resolves. Set on the
  // assistant/system placeholder (NOT the paired user message).
  request_id?: string;
  _pending?: boolean;
  _pendingKind?: 'ai_chat' | 'doc_drop' | 'doc_reconstruct';
}

export interface ChatResponse {
  answer: string;
  citations: ChatCitation[];
  path_used: string | null;
  model_used: string | null;
  run_id: string | null;
}

export async function chatWithCase(
  caseId: string,
  question: string,
  scope: ChatScope = 'all',
  history?: ChatMessage[],
  modelPath?: ChatModelPath
): Promise<ChatResponse> {
  const body: Record<string, unknown> = { question, scope };
  if (modelPath && modelPath !== 'auto') {
    body.model_path = modelPath;
  }
  if (history && history.length) {
    body.history = history.map((m) => ({ role: m.role, content: m.content }));
  }
  const { data } = await apiClient.post<ChatResponse>(
    `/cases/${caseId}/chat`,
    body
  );
  return data;
}
export async function getChatHistory(caseId: string): Promise<ChatMessage[]> {
  const { data } = await apiClient.get<{ messages: ChatMessage[] }>(
    `/cases/${caseId}/chat`
  );
  return data.messages || [];
}

/** Download the whole-conversation chat history as a Word (.docx) document.
 * Mirrors `downloadReviewRecordDocx`'s blob-download pattern. */
export async function downloadChatDocx(
  caseId: string,
  caseNumber?: string,
): Promise<Blob> {
  const resp = await apiClient.get(`/cases/${caseId}/chat/export.docx`, {
    responseType: 'blob',
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const base = (caseNumber || caseId).replace(/^DR-/, '');
  a.download = `chat-${base}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}

// response from POST /cases/{id}/chat/evaluate-vs-public -- a
// discrepancy list produced by an LLM comparing case claims/facts against
// the latest public-data results (PUBLIC path, cloud allowed).
export interface EvalDiscrepancy {
  claim: string;
  public_value: string;
  source_citation: string;
  severity_note: string;
}

export interface EvaluateVsPublicResponse {
  discrepancies: EvalDiscrepancy[];
  summary: string;
  citations: ChatCitation[];
  model_used: string | null;
  path_used: string | null;
  run_id: string | null;
}

export async function evaluateVsPublic(
  caseId: string,
  runId: string
): Promise<EvaluateVsPublicResponse> {
  const { data } = await apiClient.post<EvaluateVsPublicResponse>(
    `/cases/${caseId}/chat/evaluate-vs-public`,
    { run_id: runId }
  );
  return data;
}

// saved chat responses.
// A reviewer can persist an LLM chat answer to a case (POST), list saved
// responses for a case (GET), and delete one (DELETE, owner/admin only).
export interface SavedChatResponse {
  id: string;
  case_id: string;
  content: string;
  model_used?: string | null;
  path_used?: string | null;
  created_by?: string | null;
  created_at?: string | null;
}

export async function saveChatResponse(
  caseId: string,
  content: string,
  modelUsed?: string | null,
  pathUsed?: string | null
): Promise<SavedChatResponse> {
  const body: Record<string, unknown> = { content };
  if (modelUsed) body.model_used = modelUsed;
  if (pathUsed) body.path_used = pathUsed;
  const { data } = await apiClient.post<SavedChatResponse>(
    `/cases/${caseId}/saved-responses`,
    body
  );
  return data;
}

export async function listSavedChatResponses(
  caseId: string
): Promise<SavedChatResponse[]> {
  const { data } = await apiClient.get<{ items: SavedChatResponse[] }>(
    `/cases/${caseId}/saved-responses`
  );
  return data.items || [];
}

export async function deleteSavedChatResponse(
  savedId: string
): Promise<void> {
  await apiClient.delete(`/saved-responses/${savedId}`);
}

// ---- DataWash rebuild: message + attachments chat send ----
// Contract (PLAN.md): POST /api/v1/cases/{id}/chat with
// { message, attachments } returns { response, reconstructed, unknown_tags }.
export interface DataWashChatResponse {
  response: string;
  reconstructed: boolean;
  unknown_tags: string[];
  warnings?: string[];
  // Legacy fields still populated by the backend where applicable.
  citations?: ChatCitation[];
  path_used?: string | null;
  model_used?: string | null;
  run_id?: string | null;
}

export async function sendChatMessage(
  caseId: string,
  message: string,
  _attachments: string[] = [],
  scope: ChatScope = 'all',
  modelPath: ChatModelPath = 'auto',
): Promise<DataWashChatResponse> {
  // Backend expects {question, scope, model_path?}; map our message -> question.
  const body: Record<string, unknown> = { question: message, scope };
  if (modelPath && modelPath !== 'auto') {
    body.model_path = modelPath;
  }
  const { data } = await apiClient.post<Record<string, unknown>>(
    `/cases/${caseId}/chat`,
    body,
  );
  // Map the legacy backend response shape to our DataWash shape.
  return {
    response: (data.answer as string) || (data.response as string) || '',
    reconstructed: false,
    unknown_tags: [],
    warnings: [],
    citations: data.citations as ChatCitation[] | undefined,
    path_used: (data.path_used as string | null) ?? null,
    model_used: (data.model_used as string | null) ?? null,
    run_id: (data.run_id as string | null) ?? null,
  };
}

// Contract (PLAN.md): GET /api/v1/cases/{id}/chat/history -> { messages }.
// Falls back to the existing GET /cases/{id}/chat shape if the history
// route is absent (404 -> legacy loader).
export async function loadChatHistory(caseId: string): Promise<ChatMessage[]> {
  try {
    const { data } = await apiClient.get<{ messages: ChatMessage[] }>(
      `/cases/${caseId}/chat/history`,
    );
    return data.messages || [];
  } catch (err: any) {
    if (err?.response?.status === 404) {
      return getChatHistory(caseId);
    }
    throw err;
  }
}
